import React, { useMemo, useState } from 'react';
import RecruitmentFilterBar from '../AnalyticsRecruitment/components/RecruitmentFilterBar';
import WidgetShell from '../AnalyticsRecruitment/components/WidgetShell';
import WidgetLibraryModal from '../AnalyticsRecruitment/components/WidgetLibraryModal';
import qualityAnalyticsLibrary from './analyticsLibrary';
import { getAnalyticsScreenDefaults, ANALYTICS_SCREEN_IDS } from '../../analyticsScreenDefaults';
import { getAnalyticsToolbarActions } from '../../analyticsToolbar';
import {
  buildScreenFilterModels,
  canUseMaterializedAnalyticsBundle,
  getSupportedAnalyticsFilterState,
  getLocalizedGroupByOptions,
  normalizeAnalyticsScreenFilterOptions,
  useSessionAnalyticsFilters
} from '../../analyticsFilters';
import {
  createUnavailableAnalyticsBundle,
  mergeAnalyticsWidgetNode,
  useQueriedAnalyticsWidgetData,
  useAnalyticsScreenFilterOptions,
  useAnalyticsWidgetData,
  useSharedAnalyticsDashboard
} from '../../analyticsRtdb';
import { createVisualModel, ANALYTICS_DATA_STATUS, ANALYTICS_DATA_LAYERS } from '../../visuals/visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from '../../visuals/graphCatalog';
import { getCompatibleGraphs, resolveVisualRenderer } from '../../visuals/visualRegistry';
import UnavailableVisual from '../../visuals/UnavailableVisual';
import useCanonicalAnalyticsExport from '../../useCanonicalAnalyticsExport';

const ADJACENT_WIDGETS = {
  early_quality_index: 'source_quality',
  source_quality: 'early_quality_index'
};
const SCREEN_DEFAULTS = getAnalyticsScreenDefaults(ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES);
const BACKEND_WIDGET_IDS = new Set([
  'onboarding_health',
  'early_quality_index',
  'retention_timeline',
  'source_quality',
  'probation_performance'
]);

const COPY = {
  en: {
    library: {
      title: 'Widget Library',
      subtitle: 'Quality of Hires Beta uses employee outcome fields where available and clearly marks proxy-only metrics.',
      close: 'Close',
      add: 'Add widget',
      added: 'Added',
      exampleDataReady: 'Data available',
      exampleDataMissing: 'Not available yet',
      graphCount: (count) => `${count} graph${count === 1 ? '' : 's'}`,
      categories: {
        quality_hires_core: 'Quality of Hires Beta'
      },
      dataLayers: {
        [ANALYTICS_DATA_LAYERS.NATIVE]: 'Native',
        [ANALYTICS_DATA_LAYERS.CONNECTED]: 'Connected',
        [ANALYTICS_DATA_LAYERS.HYBRID]: 'Connected / proxy'
      }
    },
    menu: {
      settings: 'Widget settings',
      chartType: 'Chart type',
      groupBy: 'Group by',
      cardSize: 'Card size',
      resize: 'Resize widget',
      compact: 'Compact',
      medium: 'Medium',
      'three-quarter': 'Three quarter',
      large: 'Large',
      unavailable: 'No compatible data is available for this graph yet.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.STACKED_BAR_LINE]: 'Cohort quality',
        [ANALYTICS_GRAPH_TYPES.RADAR]: 'Radar chart',
        [ANALYTICS_GRAPH_TYPES.RETENTION_HEATMAP]: 'Retention heatmap',
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'Source scatter'
      }
    }
  },
  ar: {
    library: {
      title: 'مكتبة العناصر',
      subtitle: 'جودة التعيينات التجريبية تستخدم بيانات نتائج الموظفين عند توفرها وتوضح المقاييس البديلة.',
      close: 'إغلاق',
      add: 'إضافة عنصر',
      added: 'مضاف',
      exampleDataReady: 'البيانات متاحة',
      exampleDataMissing: 'غير متاحة بعد',
      graphCount: (count) => `${count} رسم`,
      categories: {
        quality_hires_core: 'جودة التعيين - تجريبي'
      },
      dataLayers: {
        [ANALYTICS_DATA_LAYERS.NATIVE]: 'محلي',
        [ANALYTICS_DATA_LAYERS.CONNECTED]: 'متصل',
        [ANALYTICS_DATA_LAYERS.HYBRID]: 'متصل / بديل محلي'
      }
    },
    menu: {
      settings: 'إعدادات العنصر',
      chartType: 'نوع الرسم',
      groupBy: 'التجميع حسب',
      cardSize: 'حجم البطاقة',
      resize: 'تغيير حجم العنصر',
      compact: 'مصغر',
      medium: 'متوسط',
      'three-quarter': 'ثلاثة أرباع',
      large: 'كبير',
      unavailable: 'لا توجد بيانات متوافقة لهذا الرسم حتى الآن.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.STACKED_BAR_LINE]: 'جودة المجموعة',
        [ANALYTICS_GRAPH_TYPES.RADAR]: 'مخطط راداري',
        [ANALYTICS_GRAPH_TYPES.RETENTION_HEATMAP]: 'خريطة الاحتفاظ',
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'مبعثر المصادر'
      }
    }
  }
};

const AnalyticsQuality = ({ isRTL = false, companyUID = '', analyticsActorId = 'system' }) => {
  const copy = isRTL ? COPY.ar : COPY.en;
  const [libraryOpen, setLibraryOpen] = useState(false);
  const { filterState, setFilterValue } = useSessionAnalyticsFilters({ screenId: SCREEN_DEFAULTS.screenId });
  const {
    visibleWidgetIds,
    widgetPrefs,
    mutateDashboardState,
    resetDashboardState,
    dashboardReady
  } = useSharedAnalyticsDashboard({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    defaultVisibleWidgetIds: SCREEN_DEFAULTS.defaultVisibleWidgetIds,
    defaultWidgetPrefs: SCREEN_DEFAULTS.defaultWidgetPrefs,
    locked: SCREEN_DEFAULTS.locked,
      updatedBy: analyticsActorId
  });
  const { screenFilterNode: remoteFilterNode, loading: remoteFilterLoading } = useAnalyticsScreenFilterOptions({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId
  });
  const { widgetNodes, loading: widgetLoading } = useAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetIds: dashboardReady ? visibleWidgetIds : []
  });
  const fallbackFilterOptions = useMemo(() => ({}), []);
  const filterOptions = useMemo(
    () =>
      normalizeAnalyticsScreenFilterOptions({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        remoteOptions: remoteFilterNode?.options || {},
        fallbackOptions: fallbackFilterOptions
      }),
    [fallbackFilterOptions, isRTL, remoteFilterNode]
  );
  const filterModels = useMemo(
    () =>
      buildScreenFilterModels({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        filterState,
        filterOptions
      }),
    [filterOptions, filterState, isRTL]
  );
  const localizedGroupByOptions = useMemo(() => getLocalizedGroupByOptions(isRTL), [isRTL]);
  const widgetEntries = useMemo(
    () => visibleWidgetIds.map((id) => qualityAnalyticsLibrary.widgets.find((widget) => widget.id === id)).filter(Boolean),
    [visibleWidgetIds]
  );

  const widgetRuntimeConfig = useMemo(
    () =>
      Object.fromEntries(
        widgetEntries.map((entry) => {
          const prefs = widgetPrefs[entry.id] || {};
          const activeGroupBy = prefs.selectorState?.groupBy || entry.defaultGroupBy || '';
          const effectiveFilters = getSupportedAnalyticsFilterState({
            filterState,
            supportedFilters: entry.supportedFilters || [],
            groupBy: activeGroupBy
          });
          const materializedGroupBy = Array.isArray(widgetNodes[entry.id]?.supportedGroupBy)
            ? widgetNodes[entry.id].supportedGroupBy
            : [];
          const canUseMaterializedBundle = canUseMaterializedAnalyticsBundle({
            screenId: SCREEN_DEFAULTS.screenId,
            filterState,
            supportedFilters: entry.supportedFilters || [],
            supportsGroupBy: entry.supportsGroupBy,
            activeGroupBy,
            defaultGroupBy: entry.defaultGroupBy || '',
            materializedGroupBy
          });

          return [entry.id, {
            prefs,
            activeGroupBy,
            effectiveFilters,
            canUseMaterializedBundle
          }];
        })
      ),
    [filterState, widgetEntries, widgetNodes, widgetPrefs]
  );

  const { widgetNodes: queriedWidgetNodes, loading: queriedLoading } = useQueriedAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetQueryMap: Object.fromEntries(
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        return [entry.id, {
          enabled: Boolean(companyUID) && (!runtime.canUseMaterializedBundle || !hasMaterializedNode),
          filters: runtime.effectiveFilters || {},
          groupBy: runtime.activeGroupBy || ''
        }];
      })
    )
  });
  const widgetLibraryCategories = useMemo(
    () =>
      qualityAnalyticsLibrary.categories.map((category) => ({
        id: category.id,
        label: copy.library.categories[category.id] || category.label,
        widgets: category.widgets.map((widget) => ({
          id: widget.id,
          label: widget.label,
          defaultGraphLabel: copy.menu.graphs[widget.defaultGraph] || widget.defaultGraph,
          graphCount: widget.supportedGraphs.length,
          isAdded: visibleWidgetIds.includes(widget.id),
          hasReadyData: Boolean(widgetNodes[widget.id] || queriedWidgetNodes[widget.id]),
          dataLayer: widget.dataLayer,
          dataLayerLabel: copy.library.dataLayers[widget.dataLayer]
        }))
      })),
    [copy.library.categories, copy.library.dataLayers, copy.menu.graphs, queriedWidgetNodes, visibleWidgetIds, widgetNodes]
  );
  const isScreenLoading =
    Boolean(companyUID) &&
    (!dashboardReady ||
      remoteFilterLoading ||
      ((widgetLoading || queriedLoading) &&
        !Object.keys(widgetNodes).length &&
        !Object.keys(queriedWidgetNodes).length));

  const widgetModels = useMemo(
    () =>
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const prefs = runtime.prefs || widgetPrefs[entry.id] || {};
        const activeGroupBy = runtime.activeGroupBy || entry.defaultGroupBy || '';
        const effectiveFilters = runtime.effectiveFilters || {};
        const unavailableBundle = createUnavailableAnalyticsBundle({
          title: entry.label,
          annotationItems: [],
          supportedFilters: entry.supportedFilters || [],
          supportedGroupBy: entry.groupByOptions || []
        });
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        const canUseMaterializedBundle = Boolean(runtime.canUseMaterializedBundle) && hasMaterializedNode;
        const bundle = canUseMaterializedBundle
          ? mergeAnalyticsWidgetNode(widgetNodes[entry.id], unavailableBundle, {
              selectedViewId: entry.supportsGroupBy ? activeGroupBy : '', isRTL
            })
          : queriedWidgetNodes[entry.id]
            ? mergeAnalyticsWidgetNode(queriedWidgetNodes[entry.id], unavailableBundle, {
              selectedViewId: entry.supportsGroupBy ? activeGroupBy : '', isRTL
            })
            : companyUID && BACKEND_WIDGET_IDS.has(entry.id)
              ? unavailableBundle
              : unavailableBundle;
        const availableGraphIds = getCompatibleGraphs(entry, bundle, prefs.size || entry.defaultSize);
        const activeGraph = availableGraphIds.includes(prefs.activeGraph)
          ? prefs.activeGraph
          : availableGraphIds[0] || '';
        const dataset = activeGraph ? bundle.datasets[activeGraph] : null;
        return createVisualModel({
          id: entry.id,
          type: entry.visualType,
          size: prefs.size || entry.defaultSize,
          variant: prefs.variant || entry.defaultVariant,
          datasets: bundle.datasets,
          datasetShape: dataset?.shape || '',
          dataset,
          activeGraph,
          availableGraphs: availableGraphIds.map((graphId) => ({
            id: graphId,
            label: copy.menu.graphs[graphId] || graphId
          })),
          dataStatus:
            bundle.dataStatus === ANALYTICS_DATA_STATUS.READY && dataset
              ? ANALYTICS_DATA_STATUS.READY
              : bundle.dataStatus,
          title: bundle.title || entry.label,
          annotations: bundle.annotationItems || [],
          meta: {
            dataSemantics: entry.dataSemantics,
            graphConfig: entry.graphConfig,
            activeGroupBy,
            supportedGroupBy: bundle.supportedGroupBy || entry.groupByOptions || []
          }
        });
      }),
    [copy.menu.graphs, isRTL, queriedWidgetNodes, widgetEntries, widgetNodes, widgetPrefs, widgetRuntimeConfig]
  );
  const {
    exporting,
    exportMode,
    handleExportAction
  } = useCanonicalAnalyticsExport({
    screenId: SCREEN_DEFAULTS.screenId,
    companyUID,
    analyticsActorId,
    filterState,
    widgetModels,
    language: isRTL ? 'ar' : 'en'
  });
  const toolbarActions = useMemo(
    () => getAnalyticsToolbarActions({
      isRTL,
      locked: SCREEN_DEFAULTS.locked,
      allowExport: true,
      exporting,
      exportMode
    }),
    [exportMode, exporting, isRTL]
  );

  const updateWidgetSize = (id, nextSize) => {
    mutateDashboardState((current) => {
      const nextWidgetPrefs = {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          size: nextSize
        }
      };
      const adjacentId = ADJACENT_WIDGETS[id];
      if (adjacentId && nextSize === 'three-quarter') {
        nextWidgetPrefs[adjacentId] = {
          ...(current.widgetPrefs[adjacentId] || {}),
          size: 'compact'
        };
      }
      if (adjacentId && current.widgetPrefs[adjacentId]?.size === 'three-quarter' && nextSize !== 'compact') {
        nextWidgetPrefs[adjacentId] = {
          ...(current.widgetPrefs[adjacentId] || {}),
          size: 'medium'
        };
      }
      return {
        ...current,
        widgetPrefs: nextWidgetPrefs
      };
    });
  };

  const updateChartType = (id, nextGraph) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          activeGraph: nextGraph
        }
      }
    }));
  };

  const updateGroupBy = (id, nextGroupBy) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          selectorState: {
            ...(current.widgetPrefs[id]?.selectorState || {}),
            groupBy: nextGroupBy
          }
        }
      }
    }));
  };

  const addWidget = (id) => {
    const entry = qualityAnalyticsLibrary.widgets.find((widget) => widget.id === id);
    mutateDashboardState((current) => {
      if (current.visibleWidgetIds.includes(id)) return current;

      return {
        visibleWidgetIds: [...current.visibleWidgetIds, id],
        widgetPrefs: {
          ...current.widgetPrefs,
          [id]: current.widgetPrefs[id] || {
            size: entry?.defaultSize || 'medium',
            activeGraph: entry?.defaultGraph || ''
          }
        }
      };
    });
  };

  const handleToolbarAction = (actionId) => {
    if (handleExportAction(actionId)) {
      return;
    }

    if (actionId === 'add_widget') {
      setLibraryOpen(true);
      return;
    }
    if (actionId === 'reset_layout') {
      resetDashboardState();
    }
  };

  return (
    <section className={`analytics-recruitment${isRTL ? ' rtl' : ''}`}>
      {isScreenLoading ? (
        <div className="data-analytics-loading-screen">
          <i className="fa-solid fa-spinner fa-spin" />
          <p>{isRTL ? 'يرجى الانتظار' : 'Please Wait'}</p>
        </div>
      ) : (
        <>
      <RecruitmentFilterBar
        filters={filterModels}
        actions={toolbarActions}
        onAction={handleToolbarAction}
        onFilterChange={setFilterValue}
        isRTL={isRTL}
      />

      <div className="analytics-recruitment-canvas">
        {widgetModels.map((model) => {
          const Renderer = model.dataset ? resolveVisualRenderer(model.activeGraph, model.datasetShape) : null;
          const adjacentId = ADJACENT_WIDGETS[model.id];
          const pairedCompact = adjacentId ? widgetPrefs[adjacentId]?.size === 'three-quarter' : false;
          const groupByOptions = localizedGroupByOptions.filter((option) =>
            (model.meta.supportedGroupBy || []).includes(option.id)
          );

          return (
            <WidgetShell
              key={model.id}
              id={model.id}
              title={model.title}
              annotationItems={model.annotations}
              size={model.size}
              pairedCompact={pairedCompact}
              variant={model.variant}
              chartTypes={model.availableGraphs}
              activeChartType={model.activeGraph}
              groupByOptions={groupByOptions}
              activeGroupBy={model.meta.activeGroupBy}
              menuCopy={copy.menu}
              onChartTypeChange={updateChartType}
              onGroupByChange={updateGroupBy}
              onSizeChange={updateWidgetSize}
              className={`analytics-recruitment-widget-${model.id}`}
              isRTL={isRTL}
            >
              {Renderer && model.dataStatus === ANALYTICS_DATA_STATUS.READY ? (
                <Renderer model={model} isRTL={isRTL} />
              ) : (
                <UnavailableVisual message={model.annotations[0] || copy.menu.unavailable} />
              )}
            </WidgetShell>
          );
        })}
      </div>

      <WidgetLibraryModal
        isOpen={libraryOpen}
        copy={copy.library}
        categories={widgetLibraryCategories}
        onClose={() => setLibraryOpen(false)}
        onAddWidget={addWidget}
        isRTL={isRTL}
      />
        </>
      )}
    </section>
  );
};

export default AnalyticsQuality;
